import { useCallback, useRef } from 'react'
import { useGlobalEvent } from '@highlight/ipc'
import type { SwipeProgressData, SwipeTarget } from '@shared/sidepanel/swipe'
import { isViewPopout } from './surface'

/**
 * Shared vocabulary and state machine for the two-finger swipe-dismiss
 * gesture, used by both the sidepanel root and the view router so thresholds,
 * side-mirroring, and the phase machine can't drift between the two.
 */

export type { SwipeProgressData, SwipeTarget }

const SWIPE_DIRECTION_THRESHOLD = 20 // Minimum movement before determining swipe direction
const SWIPE_CLOSE_THRESHOLD = 100
const SWIPE_VELOCITY_THRESHOLD = 600

/**
 * Which axis the gesture has committed to. `null` until total movement crosses
 * {@link SWIPE_DIRECTION_THRESHOLD}; `'vertical'` means it's a scroll and the
 * rest of the gesture should be ignored.
 */
function resolveSwipeAxis(data: SwipeProgressData): 'horizontal' | 'vertical' | null {
  const absX = Math.abs(data.deltaX)
  const absY = Math.abs(data.deltaY)
  if (absX < SWIPE_DIRECTION_THRESHOLD && absY < SWIPE_DIRECTION_THRESHOLD) {
    return null
  }
  return absY > absX ? 'vertical' : 'horizontal'
}

export interface EdgeSwipe {
  /** Delta re-signed so positive always points toward the panel's screen edge (the dismiss direction). */
  edgeDeltaX: number
  edgeVelocityX: number
  /** Crossed the distance/velocity threshold toward the edge. */
  shouldClose: boolean
  /** Crossed the threshold away from the edge (forward-navigation direction). */
  shouldForward: boolean
}

/** Interpret a swipe relative to the panel's anchored side (`sideSign` from `sidepanelSideSign`). */
function interpretEdgeSwipe(data: SwipeProgressData, sideSign: 1 | -1): EdgeSwipe {
  const edgeDeltaX = data.deltaX * sideSign
  const edgeVelocityX = data.velocityX * sideSign
  return {
    edgeDeltaX,
    edgeVelocityX,
    shouldClose: edgeDeltaX >= SWIPE_CLOSE_THRESHOLD || edgeVelocityX >= SWIPE_VELOCITY_THRESHOLD,
    shouldForward: edgeDeltaX <= -SWIPE_CLOSE_THRESHOLD || edgeVelocityX <= -SWIPE_VELOCITY_THRESHOLD,
  }
}

interface UseSwipeDismissOptions {
  /**
   * Whether this consumer owns the gesture, decided once at `began` and held
   * for its lifetime — the renderer's half of the main process's own
   * begin-time target lock.
   *
   * Consumers claim on their *live* state rather than on `data.target`, which
   * is main's reading of `sidepanel.view.open` and therefore one IPC hop stale:
   * a gesture started just after a View closed arrives labelled `view`, and
   * when both consumers deferred to that label nobody handled it and the swipe
   * did nothing at all. `data` is passed so a consumer can still consult it.
   */
  claims: (data: SwipeProgressData) => boolean
  /** +1 anchored right, -1 anchored left. */
  sideSign: 1 | -1
  /**
   * Whether travel *away* from the edge (the forward-navigation direction) also
   * moves the surface. Off by default, which clamps `onMove` to the dismiss
   * direction — a consumer with nowhere to go forward has nothing to show.
   */
  allowForwardTravel?: () => boolean
  /** Fires once, when the gesture commits to the horizontal axis. */
  onHorizontalStart?: (swipe: EdgeSwipe) => void
  /** Per-frame screen-space x offset for the moving surface (0 = fully open). */
  onMove: (x: number) => void
  /** Final interpretation when a non-ignored gesture ends. */
  onEnd: (swipe: EdgeSwipe) => void
}

interface SwipeDismissControls {
  /** Ignore the remainder of any in-flight gesture (e.g. a close raced the swipe). */
  cancelGesture: () => void
}

/**
 * The swipe-dismiss phase machine: axis detection with the vertical-means-
 * scroll bailout, side-relative delta normalization, and gesture-lifetime
 * bookkeeping. Consumers supply only their policy — gating, what to do on
 * horizontal commit, per-frame movement, and the end decision.
 */
export function useSwipeDismiss({
  claims,
  sideSign,
  allowForwardTravel,
  onHorizontalStart,
  onMove,
  onEnd,
}: UseSwipeDismissOptions): SwipeDismissControls {
  // Ignore the rest of the current gesture — unclaimed, a scroll, cancelled by
  // a close, … Starts ignored so a gesture already in flight when this mounts
  // (its `began` long gone) can't be picked up half-way through.
  const ignoredRef = useRef(true)
  const axisDeterminedRef = useRef(false)

  const cancelGesture = useCallback(() => {
    ignoredRef.current = true
    axisDeterminedRef.current = false
  }, [])

  useGlobalEvent('swipe:progress', (data: SwipeProgressData) => {
    if (data.phase === 'began') {
      axisDeterminedRef.current = false
      // The gesture is the overlay panel's. A popout window hears the same
      // broadcast and must never move for it.
      ignoredRef.current = isViewPopout || !claims(data)
    } else if (data.phase === 'changed') {
      if (ignoredRef.current) return

      if (!axisDeterminedRef.current) {
        const axis = resolveSwipeAxis(data)
        if (!axis) return // Not enough movement to commit to an axis yet
        axisDeterminedRef.current = true
        // Vertical movement dominates: it's a scroll, ignore the gesture
        if (axis === 'vertical') {
          ignoredRef.current = true
          return
        }
        onHorizontalStart?.(interpretEdgeSwipe(data, sideSign))
      }

      const { edgeDeltaX } = interpretEdgeSwipe(data, sideSign)
      const travel = allowForwardTravel?.() ? edgeDeltaX : Math.max(0, edgeDeltaX)
      onMove(sideSign * travel)
    } else if (data.phase === 'ended') {
      const wasIgnored = ignoredRef.current
      ignoredRef.current = true
      axisDeterminedRef.current = false
      if (wasIgnored) return
      onEnd(interpretEdgeSwipe(data, sideSign))
    }
  })

  return { cancelGesture }
}
